import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getCalculatorBySlug } from '../calculators/registry';
import {
  consumeCalculatorSource,
  rememberCalculatorSource,
  trackAnalytics,
} from './analytics';

function calculatorIdFromPath(pathname: string): string | undefined {
  const match = /^\/calculators\/([^/]+)\/?$/.exec(pathname);
  if (!match) return undefined;
  const slug = decodeURIComponent(match[1]);
  return getCalculatorBySlug(slug)?.slug;
}

function calculatorIdFromAnchor(anchor: HTMLAnchorElement): string | undefined {
  try {
    return calculatorIdFromPath(new URL(anchor.href, window.location.origin).pathname);
  } catch {
    return undefined;
  }
}

export function AnalyticsObserver() {
  const location = useLocation();
  const activeCalculatorRef = useRef<string | undefined>(undefined);
  const startedCalculatorRef = useRef<string | undefined>(undefined);
  const searchTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const calculatorId = calculatorIdFromPath(location.pathname);
    activeCalculatorRef.current = calculatorId;
    startedCalculatorRef.current = undefined;

    if (location.pathname === '/') {
      trackAnalytics({ name: 'landing_view' });
      return;
    }

    if (calculatorId) {
      trackAnalytics({
        name: 'calculator_view',
        calculatorId,
        source: consumeCalculatorSource(),
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    function ensureCalculatorStarted(): void {
      const calculatorId = activeCalculatorRef.current;
      if (!calculatorId || startedCalculatorRef.current === calculatorId) return;
      startedCalculatorRef.current = calculatorId;
      trackAnalytics({ name: 'calculator_start', calculatorId });
    }

    function handleClick(event: MouseEvent): void {
      if (!(event.target instanceof Element)) return;

      const anchor = event.target.closest('a[href]');
      if (anchor instanceof HTMLAnchorElement) {
        const calculatorId = calculatorIdFromAnchor(anchor);
        if (calculatorId) {
          if (anchor.closest('.related-links')) {
            rememberCalculatorSource('related');
            trackAnalytics({ name: 'related_calculator_click', calculatorId });
          } else if (anchor.closest('.search-results')) {
            rememberCalculatorSource('search');
          } else if (location.pathname === '/calculators') {
            rememberCalculatorSource('home');
          }
        }
      }

      const button = event.target.closest('button');
      if (
        button instanceof HTMLButtonElement
        && button.textContent?.trim() === '결과 복사'
        && activeCalculatorRef.current
      ) {
        trackAnalytics({ name: 'copy_result', calculatorId: activeCalculatorRef.current });
      }
    }

    function handleInteraction(event: Event): void {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('.calculator-form')) {
        ensureCalculatorStarted();
      }

      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.getAttribute('aria-label') !== '계산기 검색') return;

      if (searchTimerRef.current !== undefined) {
        window.clearTimeout(searchTimerRef.current);
      }

      const hasQuery = event.target.value.trim().length > 0;
      if (!hasQuery) {
        searchTimerRef.current = undefined;
        return;
      }

      searchTimerRef.current = window.setTimeout(() => {
        trackAnalytics({
          name: 'search_used',
          queryMatched: document.querySelector('.search-results .tool-link') !== null,
        });
        searchTimerRef.current = undefined;
      }, 500);
    }

    function handleSubmit(event: SubmitEvent): void {
      if (!(event.target instanceof HTMLFormElement)) return;
      if (!event.target.matches('.calculator-form')) return;

      ensureCalculatorStarted();
      const calculatorId = activeCalculatorRef.current;
      if (!calculatorId) return;

      window.setTimeout(() => {
        if (
          activeCalculatorRef.current === calculatorId
          && document.querySelector('.calculator-results .result-list')
        ) {
          trackAnalytics({ name: 'calculator_complete', calculatorId });
        }
      }, 0);
    }

    document.addEventListener('click', handleClick);
    document.addEventListener('input', handleInteraction);
    document.addEventListener('change', handleInteraction);
    document.addEventListener('submit', handleSubmit);

    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('input', handleInteraction);
      document.removeEventListener('change', handleInteraction);
      document.removeEventListener('submit', handleSubmit);
      if (searchTimerRef.current !== undefined) {
        window.clearTimeout(searchTimerRef.current);
        searchTimerRef.current = undefined;
      }
    };
  }, [location.pathname]);

  return null;
}
